import { Box } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, { PageHead, SearchBox, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const sameDay = (a, b) =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

/* Full Monday-to-Sunday week of the current week, computed rather than
   pinned. Installs get booked on any day, weekends included, so a Mon-Fri
   strip was silently hiding real arrivals that happened to land on a
   weekend. */
const weekDaysFor = (weekOffset) => {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7) + weekOffset * 7);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const PAGE_SIZE = 15;

/* TC2. Booked by the Ops Lead. Costs are reimbursed against the checklist. */
const MyTravel = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  // Separate from weekOffset: this pages the DashTable list further down the
  // page, unrelated to the week-strip Previous/Next above.
  const [listPagination, setListPagination] = useState(null);
  const [listPage, setListPage] = useState(1);
  const [search, setSearch] = useState("");
  const searchMounted = useRef(false);

  const fetchRows = async (targetPage = listPage, targetSearch = search) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchMyTravel(token, { page: targetPage, limit: PAGE_SIZE, search: targetSearch });
      setRows(res.data || []);
      setSummary(res.summary || null);
      setListPagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching your travel");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(listPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPage]);

  // Debounced so every keystroke doesn't fire a request; resets to page 1
  // since a filtered result set has its own page count. Skips its first run
  // so mount doesn't double-fetch alongside the [listPage] effect above.
  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true;
      return;
    }
    const t = setTimeout(() => {
      if (listPage === 1) fetchRows(1, search);
      else setListPage(1);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const days = weekDaysFor(weekOffset);
  const isCurrentWeek = weekOffset === 0;
  const weekRangeLabel = `${days[0].toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${days[
    days.length - 1
  ].toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`;

  const kpis = summary
    ? [
        { label: "Jobs assigned", value: summary.jobs ?? rows.length },
        { label: "This week", value: summary.thisWeek },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="My travel"
        lede="Booked by the Ops Lead. Costs are reimbursed against your submitted checklist."
        kpis={kpis}
      />

      <Box sx={{ marginBottom: "16px" }}>
        <SearchBox
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by clinic name"
          width={420}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "12px",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setWeekOffset((w) => w - 1)}
          sx={{
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "6px",
            bgcolor: UI.surface,
            color: UI.ink,
            fontSize: 13,
            padding: { xs: "10px 14px", sm: "4px 10px" },
            minHeight: { xs: "42px", sm: "auto" },
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          ‹ Previous
        </Box>
        <Box sx={{ fontSize: 13, color: UI.ink3, minWidth: 90, textAlign: "center" }}>
          {weekRangeLabel}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => setWeekOffset((w) => w + 1)}
          sx={{
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "6px",
            bgcolor: UI.surface,
            color: UI.ink,
            fontSize: 13,
            padding: { xs: "10px 14px", sm: "4px 10px" },
            minHeight: { xs: "42px", sm: "auto" },
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Next ›
        </Box>
        {!isCurrentWeek && (
          <Box
            component="button"
            type="button"
            onClick={() => setWeekOffset(0)}
            sx={{
              border: "none",
              bgcolor: "transparent",
              color: UI.p,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
              textDecoration: "underline",
            }}
          >
            Today
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)", md: "repeat(7, 1fr)" },
          gap: "10px",
          marginBottom: "28px",
        }}
      >
        {days.map((day) => {
          const events = rows.filter(
            (r) => r.arrivalDate && sameDay(new Date(r.arrivalDate), day),
          );
          return (
            <Box
              key={day.toISOString()}
              sx={{
                bgcolor: UI.surface,
                border: "1px solid",
                borderColor: UI.line,
                borderRadius: "8px",
                minHeight: { xs: "auto", sm: 150 },
              }}
            >
              <Box
                sx={{
                  padding: { xs: "10px 13px", sm: "9px 11px" },
                  borderBottom: "1px solid",
                  borderBottomColor: UI.line,
                  bgcolor: UI.bg2,
                  fontSize: { xs: 13, sm: 12 },
                  fontWeight: 500,
                  color: UI.ink,
                }}
              >
                {day.toLocaleDateString("en-IN", { weekday: "short" })}
                <Box
                  component="small"
                  sx={{
                    display: "block",
                    color: UI.ink3,
                    fontWeight: 400,
                    fontSize: { xs: 12, sm: 11 },
                  }}
                >
                  {day.toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                  })}
                </Box>
              </Box>
              <Box sx={{ padding: "8px" }}>
                {events.length === 0 ? (
                  <Box sx={{ fontSize: { xs: 12, sm: 11 }, color: UI.ink4 }}>No travel</Box>
                ) : (
                  events.map((r) => (
                    <Box
                      key={r.orderId}
                      onClick={() => navigate(`/dashboard/orders/${r.orderId}`)}
                      sx={{
                        bgcolor: UI.p50,
                        borderLeft: "2px solid",
                        borderLeftColor: UI.p,
                        borderRadius: "0 4px 4px 0",
                        padding: { xs: "10px 12px", sm: "7px 9px" },
                        marginBottom: "7px",
                        fontSize: { xs: 13, sm: 11 },
                        cursor: "pointer",
                        "&:hover": { bgcolor: UI.p100 },
                      }}
                    >
                      <Box sx={{ fontWeight: 500, color: UI.pDark }}>
                        {(r.city || "").split(",")[0]}, {r.clinicName}
                      </Box>
                      <Box sx={{ color: UI.ink3, marginTop: "2px" }}>
                        {r.slot || shortDate(r.arrivalDate)}
                        {r.inaugurationDate
                          ? ` · inauguration ${shortDate(r.inaugurationDate)}`
                          : ""}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <DashTable
        columns={[
          { label: "Clinic" },
          { label: "City" },
          { label: "State" },
          { label: "Arrival" },
          { label: "Checklist" },
        ]}
        rows={rows.map((r) => ({
          id: r.orderId,
          cells: [
            r.clinicName,
            r.city,
            r.state,
            r.arrivalDate ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-end" }}>
                {shortDate(r.arrivalDate)}
                {r.arrivalBeforeDelivery && (
                  <Tag tone="b">Before delivery ({shortDate(r.expectedDelivery)})</Tag>
                )}
              </Box>
            ) : (
              "Not scheduled"
            ),
            r.checklistOf ? (
              r.checklistDone < r.checklistOf ? (
                <Tag tone="b">
                  {r.checklistDone} of {r.checklistOf}
                </Tag>
              ) : (
                <Tag tone="g">Submitted</Tag>
              )
            ) : (
              <Tag tone="i">Not started</Tag>
            ),
          ],
        }))}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="No travel booked."
        errorText={loadFailed ? "Could not load your travel." : null}
        onRetry={fetchRows}
        foot={`${rows.length} job${rows.length === 1 ? "" : "s"} assigned to you`}
      />

      {listPagination && listPagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(listPagination.page - 1) * listPagination.limit + 1}–
            {Math.min(listPagination.page * listPagination.limit, listPagination.total)} of{" "}
            {listPagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {listPagination.page} of {listPagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setListPage((p) => Math.max(p - 1, 1))}
                disabled={listPagination.page <= 1}
                sx={btnSx("ghost", listPagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setListPage((p) => Math.min(p + 1, listPagination.totalPages))}
                disabled={listPagination.page >= listPagination.totalPages}
                sx={btnSx("ghost", listPagination.page >= listPagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default MyTravel;
